import { useState, type FormEvent } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { homePathForRole, useAuth, usesAdminShell } from '../auth/AuthContext'
import { PHONE_HELP, toE164 } from '../lib/phone'
import { DEMO_PASSWORD } from '../lib/mock/data'

const ADMIN_SHELL_PREFIXES = ['/admin', '/materials', '/finance']

function isAdminShellPath(path: string): boolean {
  return ADMIN_SHELL_PREFIXES.some((p) => path === p || path.startsWith(p + '/'))
}

export function LoginPage() {
  const { status, role, signIn, demoMode, mustChangePassword } = useAuth()
  const location = useLocation()
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (status === 'signedIn') {
    // `from` is where an expired session was interrupted. It is only worth
    // honouring when it belongs to this person's side of the app: a coach page
    // left behind by someone else must not become Gina's or Pascal's landing.
    const from = (location.state as { from?: string } | null)?.from
    const fromFitsRole = from !== undefined && isAdminShellPath(from) === usesAdminShell(role)
    const target = mustChangePassword
      ? '/set-password'
      : fromFitsRole
        ? from
        : homePathForRole(role)
    return <Navigate to={target} replace />
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    const e164 = toE164(phone)
    if (!e164) {
      setError(PHONE_HELP)
      return
    }
    setBusy(true)
    const result = await signIn(phone, password)
    setBusy(false)
    if (!result.ok) setError(result.error ?? 'Sign in failed.')
  }

  return (
    <div className="auth">
      <div className="auth__panel">
        <div className="auth__logo">
          <span className="auth__mark">LF</span>
          <p className="auth__title">La Folie Coach Portal</p>
          <p className="auth__tagline">Your events, your training, your pay. In one place.</p>
        </div>

        <form className="auth__card" onSubmit={onSubmit} noValidate>
          <div className="field">
            <label htmlFor="phone">Mobile number</label>
            <input
              id="phone"
              name="phone"
              type="tel"
              inputMode="tel"
              autoComplete="username"
              placeholder="050 123 0001"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
            />
            <span className="field__help">
              This is your username. Use the number La Folie has for you.
            </span>
          </div>

          <div className="field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {error ? (
            <p className="field__error" role="alert" style={{ marginBottom: 'var(--sp-3)' }}>
              {error}
            </p>
          ) : null}

          <button type="submit" className="btn btn--block" disabled={busy}>
            {busy ? 'Signing in' : 'Sign in'}
          </button>

          <p className="auth__foot">
            Forgot your password? Message Michel on WhatsApp and he will reset it for you.
          </p>
        </form>

        {demoMode ? <DemoHint onPick={setPhone} onPassword={setPassword} /> : null}
      </div>
    </div>
  )
}

const DEMO_ACCOUNTS = [
  { phone: '+971501230001', label: 'Layla Mock', role: 'Coach, senior' },
  { phone: '+971501230007', label: 'Hana Seed', role: 'Candidate, first login' },
  { phone: '+971501230100', label: 'Michel Mock', role: 'Admin' },
  { phone: '+971501230102', label: 'Pascal Demo', role: 'Finance' },
]

function DemoHint({
  onPick,
  onPassword,
}: {
  onPick: (phone: string) => void
  onPassword: (password: string) => void
}) {
  return (
    <div className="notice notice--warning" style={{ marginTop: 'var(--sp-4)' }}>
      <p className="notice__title">Demo mode</p>
      <div className="notice__body">
        <p className="small">
          No Supabase project is connected, so this runs on the bundled demo dataset. Password for
          every demo account is <span className="mono">{DEMO_PASSWORD}</span>.
        </p>
        <div className="btn-group" style={{ marginTop: 'var(--sp-3)' }}>
          {DEMO_ACCOUNTS.map((account) => (
            <button
              key={account.phone}
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => {
                onPick(account.phone)
                onPassword(DEMO_PASSWORD)
              }}
            >
              {account.label}
              <span className="faint nowrap">{account.role}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
